import { useState, useRef, useEffect, useCallback } from "react";
import { ChatMessagesContainer } from "./ChatMessagesContainer";
import { ChatInputContainer } from "./ChatInputContainer";
import { Sidebar } from "../layout/sidebar/Sidebar";
import { Loader2 } from "lucide-react";
import { chatAPI, Attachment } from "@/lib/api";
import { chatStorageAPI, type MessageMetadata } from "@/lib/chatStorage";
import { stripStudyReferences } from "@/lib/chatUtils";
import {
  initializeSourceTracking,
  getStoredSource,
  getCampaignFromURL,
  getSourceFromURL,
} from "@/lib/sourceTracking";
import { getCampaignMessage } from "@/lib/campaignMessages";
import {
  getUserLocation,
  getCachedLocation,
  type UserLocation,
} from "@/lib/ipLocationMapper";
import { trackPageView, trackChatStarted } from "@/lib/analytics";

const STREAM_UI_UPDATE_INTERVAL_MS = 180;
// How long the typing indicator waits for an ended stream's first reveal.
const FIRST_REVEAL_FALLBACK_MS = 1000;

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  attachments?: Attachment[];
  metadata?: MessageMetadata; // Include metadata for tracking (e.g., is_from_stt)
}

interface Chat {
  id: string;
  backendId?: string; // The real ID from backend, while id might be temporary
  title: string;
  lastMessage: string;
  timestamp: Date;
  unread?: boolean;
  messages: Message[];
}

export function ChatInterface() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const [currentMessages, setCurrentMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingAssistantMessageId, setStreamingAssistantMessageId] =
    useState<string | undefined>(undefined);
  const [awaitingAssistantRevealMessageId, setAwaitingAssistantRevealMessageId] =
    useState<string | undefined>(undefined);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [hasUserSentFirstMessage, setHasUserSentFirstMessage] = useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState<boolean | null>(
    null
  ); // null = not checked yet
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [campaignPending, setCampaignPending] = useState(false);
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const handleSendMessageRef = useRef<typeof handleSendMessage>(null!);
  const campaignMessageRef = useRef<string | null>(null);
  const campaignProcessedRef = useRef(false);
  const pageViewTrackedRef = useRef(false);
  const awaitingAssistantRevealMessageIdRef = useRef<string | undefined>(
    undefined
  );

  const CONSENT_KEY = "nightingale_patient_consent"; // localStorage key for consent
  const CONSENT_VERSION = "v1"; // Version to invalidate old consents if terms change

  const clearAwaitingAssistantReveal = useCallback(() => {
    awaitingAssistantRevealMessageIdRef.current = undefined;
    setAwaitingAssistantRevealMessageId(undefined);
  }, []);

  const handleStreamingMessageFirstReveal = useCallback((messageId: string) => {
    if (awaitingAssistantRevealMessageIdRef.current !== messageId) {
      return;
    }

    clearAwaitingAssistantReveal();
    setIsTyping(false);
  }, [clearAwaitingAssistantReveal]);

  // After a stream ends, the typing indicator waits for the answer's first
  // reveal so the two don't flicker. If that reveal is never reported, stop
  // waiting after a moment instead of showing the indicator forever.
  useEffect(() => {
    if (isStreaming || !awaitingAssistantRevealMessageId) {
      return;
    }
    const messageId = awaitingAssistantRevealMessageId;
    const timer = setTimeout(() => {
      if (awaitingAssistantRevealMessageIdRef.current !== messageId) {
        return;
      }
      console.debug("First reveal not reported; hiding the typing indicator", messageId);
      clearAwaitingAssistantReveal();
      setIsTyping(false);
    }, FIRST_REVEAL_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [isStreaming, awaitingAssistantRevealMessageId, clearAwaitingAssistantReveal]);

  // Capture campaign message during render (before URL is cleaned by effects)
  // This persists across React Strict Mode's simulated remount
  if (!campaignProcessedRef.current && campaignMessageRef.current === null) {
    const campaignId = getCampaignFromURL();
    if (campaignId) {
      campaignMessageRef.current = getCampaignMessage(campaignId);
    }
  }

  // Helper to determine what messages to show
  const messages = activeChat ? currentMessages : [];

  // Update current messages when active chat changes
  // Skip sync during streaming to prevent race condition where stale chat.messages
  // overwrites the more recent currentMessages being updated by the stream
  useEffect(() => {
    if (activeChat && !isStreaming) {
      const chat = chats.find((c) => c.id === activeChat);
      if (chat) {
        setCurrentMessages(chat.messages);
      } else {
        // Don't clear messages if we're waiting for a new chat to be added
        // This fixes the issue where clicking buttons doesn't show messages
        if (!activeChat.startsWith("temp-")) {
          setCurrentMessages([]);
        }
      }
    } else if (!activeChat) {
      setCurrentMessages([]);
    }
  }, [activeChat, chats, isStreaming]);

  // Handle browser back button navigation
  useEffect(() => {
    const handlePopState = () => {
      // When user presses back button, always navigate to root/welcome screen
      // This prevents navigation to external pages (like Google) from new chats
      if (activeChat) {
        // Stop any ongoing streaming
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
          setIsStreaming(false);
          setIsTyping(false);
          setStreamingAssistantMessageId(undefined);
          clearAwaitingAssistantReveal();
        }
        // Clear active chat and messages
        setActiveChat(null);
        setCurrentMessages([]);
        setStreamingAssistantMessageId(undefined);
        clearAwaitingAssistantReveal();
      }
    };

    // Listen for browser back/forward button
    window.addEventListener("popstate", handlePopState);

    // Initialize history state on mount
    if (!window.history.state) {
      window.history.replaceState(
        { isRoot: true },
        "",
        window.location.pathname + window.location.search + window.location.hash
      );
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [activeChat, clearAwaitingAssistantReveal]);

  // Push history state when creating a new chat
  useEffect(() => {
    if (activeChat) {
      // Push a new history state when a chat becomes active
      // This allows the back button to return to the root page
      window.history.pushState(
        { fromChat: true, chatId: activeChat },
        "",
        window.location.pathname + window.location.search + window.location.hash
      );
    }
  }, [activeChat]);

  // Load chats from backend
  const loadChats = useCallback(async () => {
    try {
      const response = await chatStorageAPI.getChats();

      // Convert backend chats to frontend format
      const loadedChats = await Promise.all(
        response.chats.map(async (backendChat) => {
          try {
            // Fetch full chat with messages
            const fullChat = await chatStorageAPI.getChatWithMessages(
              backendChat.id
            );

            return {
              id: backendChat.id,
              title: backendChat.title || `Chat ${backendChat.chat_number}`,
              lastMessage:
                fullChat.messages[fullChat.messages.length - 1]?.content ||
                "New conversation",
              timestamp: new Date(backendChat.updated_at),
              messages: fullChat.messages.map((msg) => ({
                id: msg.id,
                role: msg.role as "user" | "assistant",
                content: msg.content,
                timestamp: new Date(msg.created_at),
                metadata: msg.metadata?.study_refs
                  ? { study_refs: msg.metadata.study_refs }
                  : undefined,
              })),
            };
          } catch (error) {
            console.error(`Failed to load chat ${backendChat.id}:`, error);
            return null;
          }
        })
      );

      // Filter out any failed loads and sort by timestamp
      const validChats = (
        loadedChats.filter((chat) => chat !== null) as Chat[]
      ).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      setChats(validChats);

      // Don't auto-select a chat - let user choose or create new one
    } catch (error) {
      console.error("Failed to load chats:", error);
    }
  }, []);

  // Check for existing consent on mount
  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    if (storedConsent) {
      try {
        const consent = JSON.parse(storedConsent);
        // Check if consent is valid and matches current version
        if (consent.version === CONSENT_VERSION && consent.agreed === true) {
          setHasAgreedToTerms(true);
        } else {
          setHasAgreedToTerms(false);
        }
      } catch {
        // Invalid consent data, treat as not agreed
        setHasAgreedToTerms(false);
      }
    } else {
      setHasAgreedToTerms(false);
    }
  }, []);

  // Detect user location once on mount (for all chats in this session)
  useEffect(() => {
    const detectLocation = async () => {
      try {
        const location = await getUserLocation();
        if (location) {
          setUserLocation(location);
        }
      } catch (error) {
        console.warn("[ChatInterface] Failed to detect location:", error);
      }
    };

    detectLocation();
  }, []);

  // Initialize session and load chats on mount
  useEffect(() => {
    const initializeChat = async () => {
      try {
        // Start every visit with a fresh anonymous session so chat history is
        // ephemeral per page load. Otherwise the session id persisted in
        // localStorage would resurface chats on reload — and, on a shared/kiosk
        // device, show the previous person's chats in the sidebar. This restores
        // the per-visit reset that the (now-removed) Keycloak logout path provided.
        chatStorageAPI.resetSession();

        // Initialize source tracking from URL parameters. Read the URL first:
        // initializeSourceTracking removes the parameter, and a source from the
        // URL marks a QR scan rather than a return visit.
        const sourceFromUrl = getSourceFromURL() !== null;
        const source = initializeSourceTracking();

        // Get campaign from URL (captured earlier in render, before URL cleanup)
        const campaign = getCampaignFromURL();

        // Track page view immediately (captures QR scans even if user bounces)
        // Use ref to prevent duplicate tracking in React StrictMode
        if (!pageViewTrackedRef.current) {
          pageViewTrackedRef.current = true;
          trackPageView({
            source,
            sourceFromUrl,
            campaign,
            sessionId: chatStorageAPI.getSessionId(),
          });
        }

        // Initialize session for anonymous users
        await chatStorageAPI.initSession();

        // Load existing chats
        await loadChats();
      } catch (error) {
        console.error("Failed to initialize chat:", error);
      } finally {
        setIsLoadingChats(false);
      }
    };

    initializeChat();
  }, [loadChats]);

  // Handle campaign parameter from social media ads
  useEffect(() => {
    const message = campaignMessageRef.current;

    // Skip if no campaign message or already processed
    if (!message || campaignProcessedRef.current || isLoadingChats) {
      return;
    }

    // Block interactions during delay
    setCampaignPending(true);

    // Clean URL to prevent re-trigger on refresh
    const campaignUrl = new URL(window.location.href);
    campaignUrl.searchParams.delete('campaign');
    window.history.replaceState({}, '', campaignUrl.pathname + campaignUrl.search + campaignUrl.hash);

    // Show landing page for 1.5 seconds, then auto-send
    const timer = setTimeout(() => {
      // Mark as processed when timer fires (not before!)
      // This allows Strict Mode's second effect run to set a new timer
      campaignProcessedRef.current = true;
      setCampaignPending(false);
      handleSendMessageRef.current(message);
    }, 1500);

    return () => clearTimeout(timer);
  }, [isLoadingChats]);

  // Save messages to backend with debouncing
  const saveMessagesToBackend = useCallback(
    async (
      chatId: string,
      newMessages: Array<{ role: "user" | "assistant"; content: string }>,
      isNewChat: boolean = false
    ) => {
      // Get location for saving (cache first, then React state, graceful degradation)
      const getLocationForSave = (): UserLocation | null => {
        // Check localStorage cache first (instant if exists, bypasses React state staleness)
        const cachedLocation = getCachedLocation();
        if (cachedLocation) {
          return cachedLocation;
        }

        // Fall back to React state
        if (userLocation) {
          return userLocation;
        }

        // Graceful degradation - if location isn't available yet, send without it
        return null;
      };

      try {
        // Clear any pending save timeout
        if (savingTimeoutRef.current) {
          clearTimeout(savingTimeoutRef.current);
        }

        // Debounce the save operation
        savingTimeoutRef.current = setTimeout(async () => {
          try {
            const chat = chats.find((c) => c.id === chatId);
            const title = isNewChat
              ? newMessages[0]?.content.substring(0, 50) +
                (newMessages[0]?.content.length > 50 ? "..." : "")
              : chat?.title;

            // Use the backend ID if available, otherwise don't send temporary IDs
            const validChatId =
              chat?.backendId ||
              (chatId && !chatId.startsWith("temp-") ? chatId : undefined);

            // For new chats, get location (cache or state, instant check)
            const locationToSend = isNewChat ? getLocationForSave() : undefined;

            const response = await chatStorageAPI.saveChat(
              newMessages,
              isNewChat ? undefined : validChatId,
              title,
              isNewChat ? getStoredSource() || undefined : undefined, // Only pass source for new chats
              locationToSend // Only passed for new chats, gracefully handles null
            );

            // Store the backend chat ID in metadata but keep using the temporary ID in the UI
            // This prevents the chat from disappearing when the ID changes
            if (isNewChat && response.is_new_chat) {
              // Link page view info to this chat's trace in Langfuse
              trackChatStarted({
                chatId: response.chat_id,
                sessionId: chatStorageAPI.getSessionId(),
                source: getStoredSource(),
              });

              setChats((prevChats) =>
                prevChats.map((c) =>
                  c.id === chatId
                    ? {
                        ...c,
                        backendId: response.chat_id,
                        title: title || c.title, // Update title if provided
                      }
                    : c
                )
              );

              // Generate intelligent title for new chat after initial save
              // This happens asynchronously to not block the chat
              try {
                const titleResponse = await chatStorageAPI.generateChatTitle(
                  response.chat_id
                );
                if (titleResponse && titleResponse.title) {
                  setChats((prevChats) =>
                    prevChats.map((c) =>
                      c.id === chatId
                        ? {
                            ...c,
                            title: titleResponse.title || c.title,
                          }
                        : c
                    )
                  );
                }
              } catch {
                console.log(
                  "Failed to generate intelligent title, using default"
                );
                // Title generation failed, but chat is saved - no need to throw
              }
            }
          } catch (error) {
            console.error("Failed to save messages:", error);
          }
        }, 500); // Debounce for 500ms
      } catch (error) {
        console.error("Failed to save messages:", error);
      }
    },
    [chats, userLocation]
  );

  const handleSendMessage = async (
    content: string,
    attachments?: Attachment[],
    messageMetadata?: { is_from_stt?: boolean }
  ) => {
    setStreamingAssistantMessageId(undefined);
    clearAwaitingAssistantReveal();

    // Mark that user has sent their first message
    if (!hasUserSentFirstMessage) {
      setHasUserSentFirstMessage(true);
    }

    // Create new chat if needed
    let chatId = activeChat;
    let isNewChat = false;

    // Get the most up-to-date messages from the chats array, not currentMessages
    // This ensures we have all messages including recently streamed assistant messages
    const activeChat_ = chatId ? chats.find((c) => c.id === chatId) : null;
    const existingMessages = activeChat_ ? [...activeChat_.messages] : [];

    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date(),
      attachments,
      metadata: messageMetadata, // Include STT metadata for Langfuse tracking
    };

    if (!chatId) {
      isNewChat = true;
      chatId = `temp-${Date.now()}`; // Temporary ID until saved to backend

      const newChat: Chat = {
        id: chatId,
        title: content.substring(0, 50) + (content.length > 50 ? "..." : ""),
        lastMessage: content,
        timestamp: new Date(),
        messages: [newMessage], // Include the user message
      };

      // Add the new chat first
      setChats((prevChats) => [newChat, ...prevChats]);
      // Set it as active
      setActiveChat(chatId);
      // Set the messages directly
      setCurrentMessages([newMessage]);
    } else {
      // Update existing chat's messages
      setCurrentMessages((prev) => [...prev, newMessage]);
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === chatId
            ? {
                ...chat,
                messages: [...chat.messages, newMessage],
                lastMessage: content,
                timestamp: new Date(),
              }
            : chat
        )
      );
    }

    setIsTyping(true);
    setIsStreaming(true);

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    // Declare assistantMessageId outside try block so finally can access it
    let assistantMessageId: string | undefined;
    let pendingStreamUiUpdate: ReturnType<typeof setTimeout> | null = null;
    let streamedContent = "";
    let hasStartedStreaming = false;
    let didStreamFailOrAbort = false;

    try {
      // Build context messages using the captured message history
      const contextMessages: Array<{
        role: "user" | "assistant";
        content: string;
        attachments?: Attachment[];
        study_refs?: string;
      }> = [];

      // Use the existing messages we captured BEFORE adding the new user message
      // This ensures we have all the conversation history including assistant messages
      existingMessages.forEach((msg) => {
        contextMessages.push({
          role: msg.role as "user" | "assistant",
          content: msg.content,
          attachments: msg.attachments,
          study_refs: msg.metadata?.study_refs,
        });
      });

      // Add the new user message to context
      contextMessages.push({
        role: "user" as const,
        content,
        attachments: attachments,
      });

      // Create assistant message that will be updated with streaming content
      assistantMessageId = `msg-${Date.now() + 1}`;
      let needsToolCallSeparator = false;
      let lastStreamUiUpdateAt = 0;
      const updateAssistantMessageContent = (nextContent: string) => {
        // Update the assistant message with new content
        setCurrentMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: nextContent }
              : msg
          )
        );

        // Also update in chats
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, content: nextContent }
                      : msg
                  ),
                  lastMessage: nextContent,
                  timestamp: new Date(),
                }
              : chat
          )
        );
      };
      const flushStreamUi = (force = false) => {
        if (!hasStartedStreaming) {
          return;
        }

        const clearPendingUpdate = () => {
          if (pendingStreamUiUpdate) {
            clearTimeout(pendingStreamUiUpdate);
            pendingStreamUiUpdate = null;
          }
        };

        if (force) {
          clearPendingUpdate();
          lastStreamUiUpdateAt = Date.now();
          updateAssistantMessageContent(streamedContent);
          return;
        }

        const now = Date.now();
        const elapsed = now - lastStreamUiUpdateAt;

        if (elapsed >= STREAM_UI_UPDATE_INTERVAL_MS) {
          clearPendingUpdate();
          lastStreamUiUpdateAt = now;
          updateAssistantMessageContent(streamedContent);
          return;
        }

        if (!pendingStreamUiUpdate) {
          pendingStreamUiUpdate = setTimeout(() => {
            pendingStreamUiUpdate = null;
            lastStreamUiUpdateAt = Date.now();
            updateAssistantMessageContent(streamedContent);
          }, STREAM_UI_UPDATE_INTERVAL_MS - elapsed);
        }
      };
      // One id per turn: the backend stores the trace under it and /save links it.
      const turnId = crypto.randomUUID();
      const messagesToSave: Array<{
        role: "user" | "assistant";
        content: string;
        attachments?: Attachment[];
        metadata?: {
          tool_calls?: Array<{
            function: string;
            arguments: Record<string, unknown>;
            result: string;
          }>;
          is_from_stt?: boolean; // Add support for STT tracking
          turn_id?: string;
          study_refs?: string;
        };
      }> = [
        {
          role: "user",
          content,
          attachments: attachments,
          // STT flag, and the turn id that links this message to its trace
          metadata: { ...messageMetadata, turn_id: turnId },
        },
      ];
      const toolCalls: Array<{
        function: string;
        arguments: Record<string, unknown>;
        result: string;
      }> = [];
      let studyRefs: string | undefined;

      // Stream the response with abort signal
      for await (const chunk of chatAPI.streamChat(
        contextMessages,
        abortControllerRef.current.signal,
        {
          turnId,
          sessionId: chatStorageAPI.getSessionId(),
          source: getStoredSource(),
          campaign: getCampaignFromURL(),
        }
      )) {
        if (typeof chunk === "object" && chunk.type === "study_refs") {
          studyRefs = chunk.note;
          continue;
        }

        // Handle tool call events
        if (typeof chunk === "object" && chunk.type === "tool_call") {
          console.log("Tool call received:", chunk.function, chunk.arguments);
          toolCalls.push({
            function: chunk.function,
            arguments: chunk.arguments,
            result: chunk.result,
          });
          if (streamedContent.trim()) {
            needsToolCallSeparator = true;
          }
          continue;
        }

        // Handle text tokens
        const token = chunk as string;
        if (needsToolCallSeparator) {
          if (!token.startsWith("\n") && !streamedContent.endsWith("\n\n")) {
            streamedContent += streamedContent.endsWith("\n") ? "\n" : "\n\n";
          }
          needsToolCallSeparator = false;
        }
        streamedContent += token;

        // Add the assistant message on first token received
        if (!hasStartedStreaming) {
          hasStartedStreaming = true;
          // Keep isStreaming true to show stop button
          setStreamingAssistantMessageId(assistantMessageId);
          awaitingAssistantRevealMessageIdRef.current = assistantMessageId;
          setAwaitingAssistantRevealMessageId(assistantMessageId);

          const assistantMessage: Message = {
            id: assistantMessageId,
            role: "assistant",
            content: streamedContent,
            timestamp: new Date(),
          };

          // Update current messages directly
          setCurrentMessages((prev) => [...prev, assistantMessage]);

          // Also update the chat in the list
          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.id === chatId
                ? {
                    ...chat,
                    messages: [...chat.messages, assistantMessage],
                    lastMessage: streamedContent,
                    timestamp: new Date(),
                  }
                : chat
              )
          );
          lastStreamUiUpdateAt = Date.now();
        } else {
          flushStreamUi();
        }
      }

      flushStreamUi(true);
      // Saved without a reference line the model may have written itself.
      streamedContent = stripStudyReferences(streamedContent);

      if (studyRefs && hasStartedStreaming) {
        // Kept on the answer so the next request sends it back.
        const withRefs = (msg: Message) =>
          msg.id === assistantMessageId
            ? { ...msg, metadata: { ...msg.metadata, study_refs: studyRefs } }
            : msg;
        setCurrentMessages((prev) => prev.map(withRefs));
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === chatId ? { ...chat, messages: chat.messages.map(withRefs) } : chat
          )
        );
      }

      // Save both user and assistant messages to backend
      if (streamedContent) {
        messagesToSave.push({
          role: "assistant",
          content: streamedContent,
          metadata: {
            turn_id: turnId,
            ...(toolCalls.length > 0 ? { tool_calls: toolCalls } : {}),
            ...(studyRefs ? { study_refs: studyRefs } : {}),
          },
        });
      }
      await saveMessagesToBackend(chatId, messagesToSave, isNewChat);
      setIsStreaming(false); // Stop streaming when complete
    } catch (error) {
      didStreamFailOrAbort = true;
      // Check if error is due to user cancellation
      const isAbortError =
        (error instanceof DOMException && error.name === "AbortError") ||
        (typeof error === "object" &&
          error !== null &&
          "name" in error &&
          error.name === "AbortError");

      if (!isAbortError) {
        console.error("Error sending message:", error);

        // Add error message
        const errorMessage: Message = {
          id: `msg-${Date.now() + 2}`,
          role: "assistant",
          content:
            "Entschuldigung, es gab ein Problem bei der Verbindung zum Server. Bitte versuchen Sie es erneut.",
          timestamp: new Date(),
        };

        // Update current messages with error
        setCurrentMessages((prev) => [...prev, errorMessage]);

        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: [...chat.messages, errorMessage],
                  lastMessage: errorMessage.content,
                  timestamp: new Date(),
                }
              : chat
          )
        );
      } // End of error handling condition
    } finally {
      if (pendingStreamUiUpdate) {
        clearTimeout(pendingStreamUiUpdate);
      }
      const shouldKeepTypingUntilFirstReveal =
        hasStartedStreaming &&
        streamedContent.trim().length > 0 &&
        !didStreamFailOrAbort &&
        awaitingAssistantRevealMessageIdRef.current === assistantMessageId;

      if (!shouldKeepTypingUntilFirstReveal) {
        setIsTyping(false);
        clearAwaitingAssistantReveal();
      }
      setIsStreaming(false);
      setStreamingAssistantMessageId(undefined);
      abortControllerRef.current = null;
    }
  };

  // Keep ref updated with latest handleSendMessage
  handleSendMessageRef.current = handleSendMessage;

  const handleStopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      console.log("Stopping chat stream...");
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsStreaming(false);
      setIsTyping(false);
      setStreamingAssistantMessageId(undefined);
      clearAwaitingAssistantReveal();
    }
  }, [clearAwaitingAssistantReveal]);

  const handleNewChat = () => {
    // Stop any ongoing streaming
    handleStopStreaming();
    // Simply clear active chat and messages
    setActiveChat(null);
    setCurrentMessages([]);
    setStreamingAssistantMessageId(undefined);
    clearAwaitingAssistantReveal();

    // Push current state to history to enable back button navigation
    // This ensures that when user presses back, they return to the welcome/root page
    if (window.history.state?.fromChat) {
      // Already on a chat page, replace the state
      window.history.replaceState(
        { isRoot: true },
        "",
        window.location.pathname + window.location.search + window.location.hash
      );
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      const chat = chats.find((c) => c.id === chatId);
      // Use backend ID if available, otherwise use the chat ID if it's not temporary
      const backendChatId =
        chat?.backendId || (!chatId.startsWith("temp-") ? chatId : null);

      if (backendChatId) {
        await chatStorageAPI.deleteChat(backendChatId);
      }

      setChats((prevChats) => prevChats.filter((chat) => chat.id !== chatId));

      if (activeChat === chatId) {
        setStreamingAssistantMessageId(undefined);
        clearAwaitingAssistantReveal();
        const remainingChats = chats.filter((c) => c.id !== chatId);
        if (remainingChats.length > 0) {
          setActiveChat(remainingChats[0].id);
        } else {
          setActiveChat(null);
        }
      }
    } catch (error) {
      console.error("Failed to delete chat:", error);
    }
  };

  const handleSelectChat = async (chatId: string) => {
    setStreamingAssistantMessageId(undefined);
    clearAwaitingAssistantReveal();
    setActiveChat(chatId);

    // Load full chat if not already loaded
    const chat = chats.find((c) => c.id === chatId);
    if (chat && chat.messages.length === 0 && !chatId.startsWith("temp-")) {
      try {
        const fullChat = await chatStorageAPI.getChatWithMessages(chatId);
        setChats((prevChats) =>
          prevChats.map((c) =>
            c.id === chatId
              ? {
                  ...c,
                  messages: fullChat.messages.map((msg) => ({
                    id: msg.id,
                    role: msg.role as "user" | "assistant",
                    content: msg.content,
                    timestamp: new Date(msg.created_at),
                    metadata: msg.metadata?.study_refs
                      ? { study_refs: msg.metadata.study_refs }
                      : undefined,
                  })),
                }
              : c
          )
        );
      } catch (error) {
        console.error("Failed to load chat messages:", error);
      }
    }
  };

  if (isLoadingChats) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ height: "100vh" }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div
      className="flex h-screen bg-(--outer-bg)"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      {/* Always show sidebar - it handles its own collapsed/expanded state */}
      <Sidebar
        chats={chats}
        activeChat={activeChat || ""}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        onDeleteChat={handleDeleteChat}
      />

      <div className="flex flex-1 flex-col h-full" style={{ minWidth: 0 }}>
        {/* Main bordered container with messages and input */}
        <div
          className="flex-1 flex flex-col bg-(--viewport-bg) lg:border-t lg:border-l lg:border-r lg:border-primary-500 lg:rounded-t-xl lg:shadow-xs lg:mr-4 lg:mt-4"
          style={{ minHeight: 0 }}
        >
          {/* Messages container - simplified for better compatibility */}
          <div className="flex-1" style={{ minHeight: 0, overflow: "hidden" }}>
            <ChatMessagesContainer
              messages={messages}
              isTyping={isTyping}
              onSendMessage={handleSendMessage}
              disabled={campaignPending}
              chatId={
                // Use backend ID for Langfuse (frontend ID is temp-xxx)
                activeChat
                  ? chats.find((c) => c.id === activeChat)?.backendId || activeChat
                  : undefined
              }
              sessionId={chatStorageAPI.getSessionId() || undefined}
              streamingMessageId={streamingAssistantMessageId}
              awaitingStreamingRevealMessageId={awaitingAssistantRevealMessageId}
              onStreamingMessageFirstReveal={handleStreamingMessageFirstReveal}
            />
          </div>

          {/* Input container - simplified positioning */}
          <div className="lg:shrink-0 fixed bottom-0 left-0 right-0 z-30 lg:static">
            <ChatInputContainer
              onSendMessage={handleSendMessage}
              onStopStreaming={handleStopStreaming}
              // The stream decides when the patient may type again; the typing
              // indicator can outlast it while the answer fades in.
              disabled={isStreaming || campaignPending}
              isStreaming={isStreaming}
              showTerms={!hasUserSentFirstMessage && hasAgreedToTerms === false}
              onAgreeToTerms={() => {
                // Save consent to localStorage
                const consent = {
                  agreed: true,
                  timestamp: new Date().toISOString(),
                  version: CONSENT_VERSION,
                };
                localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
                setHasAgreedToTerms(true);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
